import React, { useEffect, useState } from 'react';
import {
  Player,
  Card,
  GameState,
  AvailableActions,
  ActionType,
  ChiOption,
  WinResult
} from '../core/types';
import HandDisplay from './HandDisplay';
import CardComponent from './CardComponent';
import PlayerInfo from './PlayerInfo';
import MeldDisplay from './MeldDisplay';
import ActionModal from './ActionModal';
import SettlementModal from './SettlementModal';
import RuleHelpModal from './RuleHelpModal';
import SettingsModal from './SettingsModal';
import AuthModal from './AuthModal';
import RechargeModal from './RechargeModal';
import { soundManager } from '../utils/sound';
import '../styles/GameBoard.css';

interface GameBoardProps {
  gameState: GameState;
  players: Player[];
  publicCard: Card | null;
  isPublicCardDrawn: boolean;
  currentTurnPlayer: Player;
  dealerPlayer: Player;
  deckSize: number;
  currentRound: number;
  totalRounds: number;
  winResult: WinResult | null;
  humanActions: AvailableActions | null;
  botBanters: Record<string, string>;
  onPlayerDiscard: (cardId: string) => void;
  onPlayerAction: (action: ActionType, chiOption?: ChiOption) => void;
  onNextRound: () => void;
  onRestartMatch: () => void;
  gameConfig: any;
  onUpdateConfig: (cfg: any) => void;
}

const GameBoard: React.FC<GameBoardProps> = ({
  gameState,
  players,
  publicCard,
  isPublicCardDrawn,
  currentTurnPlayer,
  dealerPlayer,
  deckSize,
  currentRound,
  totalRounds,
  winResult,
  humanActions,
  botBanters,
  onPlayerDiscard,
  onPlayerAction,
  onNextRound,
  onRestartMatch,
  gameConfig,
  onUpdateConfig
}) => {
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [showChiModal, setShowChiModal] = useState<boolean>(false);
  const [showRuleHelp, setShowRuleHelp] = useState<boolean>(false);
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [showAuthModal, setShowAuthModal] = useState<boolean>(false);
  const [showRechargeModal, setShowRechargeModal] = useState<boolean>(false);
  const [bgmActive, setBgmActive] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [isManualArrange, setIsManualArrange] = useState<boolean>(() => {
    return localStorage.getItem('zipai_arrange_mode') !== 'auto';
  });
  const [manualHandColumns, setManualHandColumns] = useState<Card[][]>([]);

  // 用户与云端持久化积分状态
  const [currentUser, setCurrentUser] = useState<{ id: string; username: string; points: number } | null>(() => {
    try {
      const saved = localStorage.getItem('zipai_user');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  const humanPlayer = players[0];
  const humanHand = humanPlayer?.hand;
  const automaticHandColumns = humanPlayer?.handColumns;
  const rightBot = players[1];
  const leftBot = players[2];
  const publicCardActorName = currentTurnPlayer?.id === 'player_0' ? '你' : currentTurnPlayer?.name;
  const exposedHuxi = humanPlayer?.melds.reduce((total, meld) => total + meld.huxi, 0) || 0;

  const isHumanTurn = currentTurnPlayer?.id === 'player_0';
  const isHumanWaitingDiscard = isHumanTurn && gameState === GameState.WAITING_DISCARD;

  useEffect(() => {
    const syncFullscreenState = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', syncFullscreenState);
    return () => document.removeEventListener('fullscreenchange', syncFullscreenState);
  }, []);

  const handleToggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await document.documentElement.requestFullscreen();
      }
    } catch {
      // iOS Safari may reject arbitrary-element fullscreen requests.
    }
  };

  useEffect(() => {
    if (!isManualArrange || !humanHand || !automaticHandColumns) return;

    setManualHandColumns(previousColumns => {
      const cardsById = new Map(humanHand.map(card => [card.id, card]));
      const retainedCardIds = new Set<string>();
      const retainedColumns = previousColumns
        .map(column => column
          .filter(card => cardsById.has(card.id))
          .map(card => cardsById.get(card.id)!)
        )
        .filter(column => {
          column.forEach(card => retainedCardIds.add(card.id));
          return column.length > 0;
        });

      if (retainedCardIds.size === 0) {
        return automaticHandColumns.map(column => [...column]);
      }

      const newCards = humanHand.filter(card => !retainedCardIds.has(card.id));
      return [...retainedColumns, ...newCards.map(card => [card])];
    });
  }, [automaticHandColumns, humanHand, isManualArrange]);

  // 点击手牌选定
  const handleCardClick = (cardId: string) => {
    soundManager.playClick();
    setSelectedCardId(selectedCardId === cardId ? null : cardId);
  };

  // 双击手牌直接出牌
  const handleCardDoubleClick = (cardId: string) => {
    if (isHumanWaitingDiscard) {
      onPlayerDiscard(cardId);
      setSelectedCardId(null);
    }
  };

  // 点击出牌按钮
  const handleDiscardClick = () => {
    if (selectedCardId && isHumanWaitingDiscard) {
      onPlayerDiscard(selectedCardId);
      setSelectedCardId(null);
    }
  };

  const handleArrangeModeToggle = () => {
    const nextManual = !isManualArrange;
    if (nextManual && humanPlayer) {
      setManualHandColumns(humanPlayer.handColumns.map(column => [...column]));
    }
    setIsManualArrange(nextManual);
    localStorage.setItem('zipai_arrange_mode', nextManual ? 'manual' : 'auto');
  };

  const handleManualCardMove = (cardId: string, targetColumnIndex: number) => {
    setManualHandColumns(previousColumns => {
      const sourceColumnIndex = previousColumns.findIndex(column =>
        column.some(card => card.id === cardId)
      );
      if (sourceColumnIndex < 0 || sourceColumnIndex === targetColumnIndex) return previousColumns;

      const card = previousColumns[sourceColumnIndex].find(item => item.id === cardId);
      if (!card) return previousColumns;
      if (targetColumnIndex < previousColumns.length && previousColumns[targetColumnIndex].length >= 5) {
        return previousColumns;
      }

      const nextColumns = previousColumns.map(column => column.filter(item => item.id !== cardId));
      let adjustedTargetIndex = targetColumnIndex;
      if (nextColumns[sourceColumnIndex].length === 0) {
        nextColumns.splice(sourceColumnIndex, 1);
        if (sourceColumnIndex < targetColumnIndex) adjustedTargetIndex -= 1;
      }

      if (adjustedTargetIndex >= nextColumns.length) {
        nextColumns.push([card]);
      } else {
        nextColumns[adjustedTargetIndex] = [...nextColumns[adjustedTargetIndex], card];
      }
      return nextColumns;
    });
  };

  // 点击吃牌按钮
  const handleChiButtonClick = () => {
    if (!humanActions?.chiOptions || humanActions.chiOptions.length === 0) return;
    if (humanActions.chiOptions.length === 1) {
      onPlayerAction(ActionType.CHI, humanActions.chiOptions[0]);
    } else {
      setShowChiModal(true);
    }
  };

  // 切换背景轻音乐
  const handleToggleBgm = () => {
    const active = soundManager.toggleBGM();
    setBgmActive(active);
  };

  // 收集所有打入弃牌池的牌
  const allDiscards = players.flatMap(p => p.discards);

  return (
    <div className="game-table-container">
      {/* 顶部控制栏 */}
      <header className="table-top-bar">
        <div className="brand-logo">
          <span className="logo-icon">🎴</span>
          <span className="logo-title">经典字牌 · 雀圣风云</span>
        </div>

        <div className="round-badge">
          <span>第 <strong className="gold-text">{currentRound}</strong> / {totalRounds} 局</span>
          <span className="dealer-indicator">当前庄家: {dealerPlayer?.name}</span>
        </div>

        {/* 用户与云端积分持久化状态 */}
        <div className="user-profile-dock">
          {currentUser ? (
            <div className="user-logged-box">
              <span className="user-badge-name">👤 {currentUser.username}</span>
              <span className="user-points-badge gold-text">💰 {currentUser.points.toLocaleString()} 分</span>
              <button
                className="btn-top-recharge pulse-glow"
                onClick={() => setShowRechargeModal(true)}
                title="充值积分"
              >
                ➕ 充值
              </button>
            </div>
          ) : (
            <button className="btn-top-login" onClick={() => setShowAuthModal(true)}>
              <span aria-hidden="true">🔑</span>
              <span className="top-control-label">登录 / 注册 (送1000分)</span>
            </button>
          )}
        </div>

        <div className="top-action-buttons">
          <button
            className={`btn-icon-top ${bgmActive ? 'active-bgm' : ''}`}
            onClick={handleToggleBgm}
            title="背景音乐"
          >
            <span aria-hidden="true">{bgmActive ? '🎵' : '🔇'}</span>
            <span className="top-control-label">音乐:{bgmActive ? '开' : '关'}</span>
          </button>
          <button className="btn-icon-top" onClick={() => setShowRuleHelp(true)} title="规则指南">
            <span aria-hidden="true">📖</span><span className="top-control-label">规则</span>
          </button>
          <button className="btn-icon-top" onClick={() => setShowSettings(true)} title="设置">
            <span aria-hidden="true">⚙️</span><span className="top-control-label">设置</span>
          </button>
          <button className="btn-icon-top" onClick={onRestartMatch} title="重新开局">
            <span aria-hidden="true">🔄</span><span className="top-control-label">重开</span>
          </button>
          <button className="btn-icon-top" onClick={handleToggleFullscreen} title={isFullscreen ? '退出全屏' : '全屏'}>
            <span aria-hidden="true">{isFullscreen ? '⛶' : '⛶'}</span>
            <span className="top-control-label">{isFullscreen ? '退出全屏' : '全屏'}</span>
          </button>
        </div>
      </header>

      {/* 牌桌主体 */}
      <main className="felt-table-surface">
        {/* 对手区域 (上方左右两侧) */}
        <div className="opponents-layer">
          {leftBot && (
            <PlayerInfo
              player={leftBot}
              isActiveTurn={currentTurnPlayer?.id === leftBot.id}
              speechBubble={botBanters[leftBot.id]}
            />
          )}

          {rightBot && (
            <PlayerInfo
              player={rightBot}
              isActiveTurn={currentTurnPlayer?.id === rightBot.id}
              speechBubble={botBanters[rightBot.id]}
            />
          )}
        </div>

        {/* 中央舞台 (牌堆 + 亮张判定区 + 弃牌池) */}
        <div className="center-stage">
          {/* 牌堆 (可点击摸牌) */}
          <div
            className="deck-stack"
            title={`底牌池剩余 ${deckSize} 张`}
          >
            <div className="deck-tiles-visual">
              <div className="tile-layer layer-3"></div>
              <div className="tile-layer layer-2"></div>
              <div className="tile-layer layer-1">
                <span className="deck-count-text">🎴 {deckSize} 张</span>
              </div>
            </div>
            <span className="deck-label">底牌池</span>
          </div>

          {/* 公共亮张判定区 */}
          <div className="public-card-arena">
            {publicCard ? (
              <div className="public-card-wrapper animated-pop">
                <div className="public-card-header">
                  {isPublicCardDrawn ? `⚡ ${publicCardActorName}摸出亮牌` : `📤 ${publicCardActorName}打出亮牌`}
                </div>
                <CardComponent card={publicCard} highlight={true} />
              </div>
            ) : (
              <div className="public-card-placeholder">
                <span className="placeholder-text">判定区</span>
              </div>
            )}
          </div>

          {/* 桌面弃牌池展示 */}
          <div className="table-discard-pool">
            <div className="pool-header">臭牌 / 弃牌池 ({allDiscards.length})</div>
            <div className="discard-cards-grid">
              {allDiscards.slice(-25).map((card, idx) => (
                <div key={card.id || idx} className="discard-card-item">
                  <CardComponent card={card} isSmall={true} />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 下方真人玩家区域 */}
        <div className="player-self-arena">
          {/* 玩家状态与亮牌栏 */}
          <div className="player-self-header">
            <div className="player-self-info">
              <span className="self-avatar">{humanPlayer?.avatar}</span>
              <span className="self-name">{currentUser ? currentUser.username : humanPlayer?.name}</span>
              {humanPlayer?.isDealer && <span className="self-dealer-tag">庄</span>}
              <span className="self-stats">
                <span className="self-score">本局积分: <strong className="gold-text">{humanPlayer?.score}</strong></span>
                <span className="self-huxi">桌面胡息: <strong className="huxi-badge">{exposedHuxi} 胡</strong></span>
              </span>
            </div>

            {/* 真人已亮出的牌组 (吃、碰、提、跑，紧凑微缩展示) */}
            {humanPlayer && humanPlayer.melds.length > 0 && (
              <div className="self-exposed-melds">
                <MeldDisplay melds={humanPlayer.melds} isCompact={true} />
              </div>
            )}
          </div>

          {/* 交互操作栏 */}
          <div className="action-control-dock">
            {/* 出牌提示 (起手庄家或吃碰后需要出牌时) */}
            {isHumanWaitingDiscard && selectedCardId && (
              <button
                className="btn-dock btn-discard-card pulse-glow"
                onClick={handleDiscardClick}
              >
                📤 出选中的牌
              </button>
            )}

            {/* 判定响应动作栏 (胡 / 提 / 跑 / 碰 / 吃 / 过) */}
            {humanActions && (
              <div className="action-response-bar">
                {humanActions.canHu && (
                  <button
                    className="btn-dock btn-hu pulse-glow"
                    onClick={() => onPlayerAction(ActionType.HU)}
                  >
                    👑 胡牌！
                  </button>
                )}

                {humanActions.canTi && (
                  <button
                    className="btn-dock btn-ti"
                    onClick={() => onPlayerAction(ActionType.TI)}
                  >
                    🌀 提牌
                  </button>
                )}

                {humanActions.canPao && (
                  <button
                    className="btn-dock btn-pao"
                    onClick={() => onPlayerAction(ActionType.PAO)}
                  >
                    ⚡ 跑牌
                  </button>
                )}

                {humanActions.canPeng && (
                  <button
                    className="btn-dock btn-peng"
                    onClick={() => onPlayerAction(ActionType.PENG)}
                  >
                    ✋ 碰牌
                  </button>
                )}

                {humanActions.canChi && humanActions.chiOptions.length > 0 && (
                  <button className="btn-dock btn-chi" onClick={handleChiButtonClick}>
                    🍚 吃牌 ({humanActions.chiOptions.length}种方案)
                  </button>
                )}

                <button
                  className="btn-dock btn-pass"
                  onClick={() => onPlayerAction(ActionType.PASS)}
                >
                  ❌ 过 (不要)
                </button>
              </div>
            )}
          </div>

          {/* 手牌展示 (排柱/列式排布 + 悬停展开与放大镜) */}
          <div className="player-hand-container">
            <HandDisplay
              handColumns={isManualArrange ? manualHandColumns : humanPlayer?.handColumns || []}
              selectedCardId={selectedCardId}
              onCardClick={handleCardClick}
              onCardDoubleClick={handleCardDoubleClick}
              isManualArrange={isManualArrange}
              onManualCardMove={handleManualCardMove}
              disabled={gameState === GameState.ROUND_OVER}
            />
          </div>
        </div>
      </main>

      {/* 吃牌方案选择弹窗 */}
      {showChiModal && humanActions?.chiOptions && (
        <ActionModal
          chiOptions={humanActions.chiOptions}
          onSelectOption={opt => {
            setShowChiModal(false);
            onPlayerAction(ActionType.CHI, opt);
          }}
          onCancel={() => setShowChiModal(false)}
        />
      )}

      {/* 局数结算与大结算弹窗 */}
      {gameState === GameState.ROUND_OVER && (
        <SettlementModal
          winResult={winResult}
          players={players}
          currentRound={currentRound}
          totalRounds={totalRounds}
          onNextRound={onNextRound}
          onRestartMatch={onRestartMatch}
        />
      )}

      {/* 规则指南弹窗 */}
      {showRuleHelp && <RuleHelpModal onClose={() => setShowRuleHelp(false)} />}

      {/* 设置弹窗 */}
      {showSettings && (
        <SettingsModal
          config={gameConfig}
          isManualArrange={isManualArrange}
          onToggleArrangeMode={handleArrangeModeToggle}
          onUpdateConfig={onUpdateConfig}
          onClose={() => setShowSettings(false)}
        />
      )}

      {/* 登录/注册弹窗 */}
      {showAuthModal && (
        <AuthModal
          onClose={() => setShowAuthModal(false)}
          onLoginSuccess={user => {
            setCurrentUser(user);
          }}
        />
      )}

      {/* 充值弹窗 */}
      {showRechargeModal && (
        <RechargeModal
          currentPoints={currentUser?.points ?? 1000}
          onClose={() => setShowRechargeModal(false)}
          onRechargeSuccess={(added, newTotal) => {
            if (currentUser) {
              setCurrentUser({ ...currentUser, points: newTotal });
            }
          }}
        />
      )}
    </div>
  );
};

export default GameBoard;
